import { computed } from 'vue';
import { useUserStore } from 'src/core/stores/user';
import type {
  UserTestProfileAccess,
  UserServerResponse,
} from 'src/core/types/user';

const EUDR_PROFILE = 'EUDR';

/**
 * Profile value from a test profile entry.
 */
function getTestProfileValue(testProfile: UserTestProfileAccess): string {
  return testProfile.profile.value;
}

/**
 * Whether the customer has EUDR in their test_profiles.
 * Uses user.customer or user.supplier.customer; returns false when no customer or no test_profiles.
 */
export function hasEudrInAccess(user: UserServerResponse | undefined): boolean {
  const customer = user?.customer ?? user?.supplier?.customer;
  const testProfiles = customer?.test_profiles ?? [];
  return testProfiles.some((testProfile) => testProfile?.toUpperCase() === EUDR_PROFILE);
}

/**
 * Composable for profile-based user access (customer.test_profiles).
 * Use to gate EUDR-only UI (Parcels & DDS, ICE CoT) and to filter
 * data ingestion options (origins, test profiles).
 */
export function useUserAccess() {
  const userStore = useUserStore();

  const userTestProfilesAccess = computed(() => userStore.user?.test_profiles_access);

  const hasEudrAccess = computed(() => hasEudrInAccess(userStore.user));

  /** Profile name values the user is allowed to use (e.g. ['EUDR', 'Data Quality', 'DCF']), from customer.test_profiles. */
  const allowedTestProfiles = computed(() => {
    const customer =
      userStore.user?.customer ?? userStore.user?.supplier?.customer;
    const testProfiles = customer?.test_profiles;
    if (!testProfiles?.length) return null;
    return [...testProfiles].sort((a, b) => a.localeCompare(b));
  });

  /** Combined list of origin (country) values from test profiles that belong to the customer (customer.test_profiles only). */
  const allowedOriginCountries = computed(() => {
    const testProfiles = userTestProfilesAccess.value;
    const customer =
      userStore.user?.customer ?? userStore.user?.supplier?.customer;
    const customerProfileValues = new Set(customer?.test_profiles ?? []);
    if (!testProfiles?.length || !customerProfileValues.size) return null;
    const countryValues = new Set<string>();
    for (const testProfile of testProfiles) {
      if (
        !customerProfileValues.has(getTestProfileValue(testProfile)) ||
        !testProfile.countries?.length
      )
        continue;
        testProfile.countries.forEach((country) => countryValues.add(country.value));
    }
    return countryValues.size ? Array.from(countryValues) : null;
  });

  return {
    userTestProfilesAccess,
    hasEudrAccess,
    allowedTestProfiles,
    allowedOriginCountries,
  };
}
